import { Space, Table, Tooltip } from 'antd'
import { DateTime } from 'luxon'
import PopupMap from './PopupMap'
import { green, orange } from '@ant-design/colors'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleCheck, faCircleQuestion, faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons'
import { faFlag } from '@fortawesome/pro-duotone-svg-icons'

type GpsAction =
  | 'clocked-in'
  | 'clocked-out'
  | 'clock-in-edited'
  | 'clock-out-edited'

export type GpsLog = {
  timestamp: string
  action: GpsAction
  status: string
  latitude: number
  longitude: number
  submittedBy: string
  isFlagged: boolean
}
export type Props = {
  googleMapsApiKey: string
  logs: GpsLog[]
  hideTitle?: boolean
}

const actionNames = {
  'clocked-in': 'Clock In',
  'clocked-out': 'Clock Out',
  'clock-in-edited': 'Edited Clock In',
  'clock-out-edited': 'Edited Clock Out',
  'unbillable-started': 'Started',
  'unbillable-ended': 'Ended',
  'unbillable-edited': 'Edited'
}

const renderIcon = (value: string, item: GpsLog) => {
  let statusIcon = <></>
  switch (value) {
    case 'success':
      if (!item.isFlagged) {
        statusIcon = <FontAwesomeIcon icon={faCircleCheck} style={{ color: green.primary }} />
      }
      break
    case 'permission denied':
      statusIcon = <Tooltip title="Location permissions were denied in mobile app." placement="right">
        <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: orange.primary }} />
      </Tooltip>
      break
    default:
      statusIcon = <Tooltip title={`Status: ${value}`} placement="right">
        <FontAwesomeIcon icon={faCircleQuestion} />
      </Tooltip>
      break
  }

  let flagIcon = <></>
  if (item.isFlagged) {
    let message = ''
    if (item.action === 'clocked-in') {
      message = 'Started time on timesheet differs from when clock in was recorded'
    }
    if (item.action === 'clocked-out') {
      message = 'Ended time on timesheet differs from when clock out was recorded'
    }

    flagIcon = <Tooltip title={message} placement="right">
      <FontAwesomeIcon icon={faFlag} style={{ color: "#888" }} />
    </Tooltip>
  }

  return <Space>
    {statusIcon}
    {flagIcon}
  </Space>
}

const GpsLogComponent = ({ logs, googleMapsApiKey, hideTitle }: Props) => <Space direction="vertical" style={{ width: '100%' }}>
  {!hideTitle && <strong>GPS Event Log</strong>}
  <Table
    pagination={{ pageSize: 5 }}
    dataSource={logs.map((x, i) => ({ ...x, key: `${x.timestamp}${i}` }))}
    rowKey="key"
    columns={[
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: renderIcon
      },
      {
        title: 'Action',
        dataIndex: 'action',
        key: 'action',
        render: (value: GpsAction) => actionNames[value] ?? value
      },
      {
        title: 'Recorded At',
        dataIndex: 'timestamp',
        key: 'timestamp',
        render: (value) => {
          const timestamp = DateTime.fromISO(value)
          return timestamp.toLocal().toLocaleString(DateTime.DATETIME_MED)
        }
      },
      {
        title: 'Location',
        render: (_, x) => {
          if (x.status !== 'success') {
            return <></>
          }
          const position = { latitude: x.latitude, longitude: x.longitude }
          return <PopupMap
            apiKey={googleMapsApiKey}
            center={position}
            zoom={15}
            markers={[
              { position }
            ]}
            width={400}
            height={300}
            label={`${x.latitude.toFixed(4)}, ${x.longitude.toFixed(4)}`}
          />
        }
      },
      {
        title: 'Submitted By',
        dataIndex: 'submittedBy',
        key: 'submittedBy'
      }
    ]}
  />
</Space>
export default GpsLogComponent
